/* One definition's runs: the tolerance that judges them, what the six outcomes mean, and the ledger.
 *
 * The reference's structure, and the order is the argument. A breadcrumb back to the definitions; the
 * definition's two sides and the one control on the page; then **two panels side by side** — the
 * tolerance version in force, and the outcome vocabulary — and only then the runs themselves.
 *
 * **The vocabulary panel is not documentation, it is the screen's reason for existing.** Four of the
 * six states are terminal and two of those are *refusals*: the platform measured cheaply, concluded
 * the definition is wrong, and declined the join. A reader who reads "keys disjoint" as a failure
 * retries it, gets the identical refusal, and concludes the platform is broken. The panel says which
 * outcomes are fixed by retrying and which are fixed by editing the definition, beside the table where
 * that distinction is being made.
 *
 * **The tolerance panel carries the version number because a run cites it, frozen.** "Zero breaks in
 * March" under a 5% tolerance and under 0.1% are different facts wearing one sentence, so a tolerance
 * version is immutable once published and every run records the one that judged it. The Judged by
 * column in the table is that citation; this panel is what the citation resolves to.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { Definition, Run } from '@/shared/api/generated/reconciliation';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  can,
  CONFIGURE_RECONCILIATION_PERMISSION,
  CONFIGURE_RUN_SCHEDULE_PERMISSION,
  PUBLISH_RECONCILIATION_PERMISSION,
  RUN_RECONCILIATION_PERMISSION,
  useIdentity,
} from '@/entities/session';

import { useDefinition, useRequestRun, useRuns } from '../api/reconciliation';
import { BreakPolicyPanel } from './BreakPolicyPanel';
import { DefinitionHistory } from './DefinitionHistory';
import { PublishToleranceDialog } from './PublishToleranceDialog';
import { RunSchedulePanel } from './RunSchedulePanel';
import { breaksPath } from '../model/paths';
import {
  boundReading,
  breakTotalOf,
  formatCount,
  formatJaccard,
  hasBreaks,
  isInFlight,
  isRefusal,
  jaccardNoteKey,
  noBreaksReasonKey,
  outcomeOf,
  rowTintOf,
  sideLabel,
  toneOf,
} from '../model/reconciliation';
import type { RunTone } from '../model/reconciliation';
import { RunStateTag } from './RunStateTag';

/** The four outcomes the vocabulary panel spells out, in the order a reader meets them.
 *
 * `pending` and `running` are deliberately absent: they are not outcomes, and listing them would put
 * "it has not finished" beside three statements about what a finished run means. Derived from the two
 * refusal states plus the two real terminal ones rather than from `RUN_STATES`, so the panel cannot
 * silently gain a row for a state that is not an outcome.
 */
const VOCABULARY = ['succeeded', 'failed', 'keys_disjoint', 'keys_not_unique'] as const;

/** The tolerance version in force, field by field.
 *
 * `tolerance_version` is null only on a list response, so on this route a null is a genuine absence
 * and says so — rather than rendering an empty field list, which would look like a definition with no
 * tolerances and is a state the substrate refuses to store.
 */
function TolerancePanel({
  definition,
}: {
  readonly definition: Definition;
}): ReactElement {
  const { t } = useTranslation();
  const version = definition.tolerance_version;

  if (version === null) {
    return (
      <section className="lq-rc__panel">
        <h3 className="lq-rc__panelHead">{t('reconciliation.tolerance.unknown')}</h3>
        <p className="lq-rc__panelBody">{t('reconciliation.tolerance.unknownBody')}</p>
      </section>
    );
  }

  return (
    <section className="lq-rc__panel">
      <h3 className="lq-rc__panelHead">
        {t('reconciliation.tolerance.title', { version: version.version })}
      </h3>
      {version.fields.map((field) => {
        const bound = boundReading(field);
        return (
          <div
            /* The column pair is the identity: a version cannot carry the same pair twice, and using
               an array index would make React reuse a row across two different versions of the same
               definition. */
            key={`${field.left_column}:${field.right_column}`}
            className="lq-rc__tolRow"
          >
            <div className="lq-rc__tolPair lq-mono">
              {t('reconciliation.tolerance.pair', {
                left: field.left_column,
                right: field.right_column,
              })}
            </div>
            <div className="lq-rc__tolBound">
              <span className="lq-rc__tolKind">
                {t(`reconciliation.kind.${field.kind}`)}
              </span>
              <div className="lq-rc__tolValue lq-mono">{t(bound.key, bound.values)}</div>
            </div>
          </div>
        );
      })}
      <p className="lq-rc__panelNote">
        {t('reconciliation.tolerance.published', {
          who: version.published_by,
          when: new Date(version.published_at).toLocaleDateString(),
        })}
      </p>
      <p className="lq-rc__panelNote">{t('reconciliation.tolerance.ratioNote')}</p>
    </section>
  );
}

/** What the four terminal outcomes mean, and which fix each one calls for. */
function VocabularyPanel(): ReactElement {
  const { t } = useTranslation();

  return (
    <section className="lq-rc__panel">
      <h3 className="lq-rc__panelHead">{t('reconciliation.vocab.title')}</h3>
      {VOCABULARY.map((state) => {
        const tone: RunTone = toneOf(state);
        return (
          <div key={state} className="lq-rc__vocabRow">
            <span
              className={`lq-rc__vocabDot lq-rc__vocabDot--${tone}`}
              aria-hidden="true"
            />
            <div className="lq-rc__vocabBody">
              <strong className={`lq-rc__vocabLabel lq-rc__vocabLabel--${tone}`}>
                {t(`reconciliation.state.${state}`)}
              </strong>
              <div className="lq-rc__vocabNote">
                {t(`reconciliation.stateHint.${state}`)}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/** The outcome cell: three counts, or the reason there are none.
 *
 * `outcomeOf` returns null for every non-succeeded state, so this renders an em dash rather than
 * zeros — a refused run did not measure zero mismatches, it measured nothing. The failure message
 * underneath is the server's own sentence, which for a refusal carries the estimates or the row counts
 * that produced it: that is the actionable half, and paraphrasing it here would be a second place
 * deciding what a refusal means.
 */
function OutcomeCell({ run }: { readonly run: Run }): ReactElement {
  const { t } = useTranslation();
  const outcome = outcomeOf(run);

  return (
    <>
      {outcome === null ? (
        <span className="lq-muted">{t('reconciliation.noFigures')}</span>
      ) : (
        /* One figure per track, so each sits under its own heading. Not an interpolated
           sentence: `{matched} · {mismatched} · {missing}` rendered three numbers in a row that
           lined up with nothing, and a reader had to count separators to tell which was which. */
        <span className="lq-rc__counts lq-table__parts--cell">
          {/* **Each figure carries its own name, and that is not decoration.** The three used to
              be one string separated by `·`, which a screen reader read as a list. Splitting them
              into tracks made the separator a CSS border — visually better and, on its own, an
              accessibility regression: the accessible text became `1283402 469 33`, three numbers
              run together under a single `<th>` that names all three. The heading cannot
              disambiguate them because it is one cell, so each value says what it is. */}
          <span>
            <span className="lq-visually-hidden">
              {t('reconciliation.column.outcome.matched')}
              {': '}
            </span>
            {formatCount(outcome.matched)}
          </span>
          <span>
            <span className="lq-visually-hidden">
              {t('reconciliation.column.outcome.mismatched')}
              {': '}
            </span>
            {formatCount(outcome.mismatched)}
          </span>
          <span>
            <span className="lq-visually-hidden">
              {t('reconciliation.column.outcome.missing')}
              {': '}
            </span>
            {formatCount(outcome.missing)}
          </span>
        </span>
      )}
      {run.breaks_truncated && (
        <div className="lq-rc__truncNote">{t('reconciliation.truncatedRow')}</div>
      )}
      {typeof run.failure === 'string' && run.failure.length > 0 && (
        <div className="lq-rc__failure">{run.failure}</div>
      )}
    </>
  );
}

/** The Jaccard cell: the figure, and where it came from.
 *
 * Three decimals because the sketch's own error is a few percent above k=1024 — a fourth would claim a
 * resolution the estimator does not have. The note beneath is not decoration: on a refused run this
 * number *is* the evidence for the refusal, and on a succeeded one it is a measurement that preceded
 * the join. Printing the same figure with no provenance would make those look like one thing.
 */
function JaccardCell({ run }: { readonly run: Run }): ReactElement {
  const { t } = useTranslation();
  const estimate = run.jaccard_estimate;
  const noteKey = jaccardNoteKey(run);

  if (estimate === null || estimate === undefined || noteKey === null) {
    return <span className="lq-muted">{t('reconciliation.noFigures')}</span>;
  }
  return (
    <>
      {formatJaccard(estimate)}
      <div className="lq-cell__secondary">{t(noteKey)}</div>
    </>
  );
}

export function DefinitionRuns(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { definitionId = '' } = useParams<{ definitionId: string }>();
  const definition = useDefinition(definitionId);
  const runs = useRuns(definitionId);
  const request = useRequestRun();
  const identity = useIdentity();
  const [queued, setQueued] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const mayRun = can(identity.data, RUN_RECONCILIATION_PERMISSION);
  const mayPublish = can(identity.data, PUBLISH_RECONCILIATION_PERMISSION);
  /* Two permissions, because they gate two different resource types: the break policy is an action
     on the reconciliation, the schedule is its own type. A deployment may grant "reconcile now" and
     withhold "reconcile every night for ever", so the panels ask separately. */
  const mayConfigurePolicy = can(identity.data, CONFIGURE_RECONCILIATION_PERMISSION);
  const mayConfigureSchedule = can(identity.data, CONFIGURE_RUN_SCHEDULE_PERMISSION);

  const name = definition.isSuccess ? definition.data.name : definitionId;
  const version = definition.isSuccess ? definition.data.tolerance_version : null;

  return (
    <div className="lq-rc">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.reconciliation}>{t('reconciliation.title')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{name}</span>
      </nav>

      <header className="lq-page-header">
        <div>
          <h1 className="lq-page-header__title lq-rc__detailTitle">{name}</h1>
          {definition.isSuccess && (
            <p className="lq-page-header__lede lq-rc__sides">
              {t('reconciliation.sides', {
                left: sideLabel(
                  definition.data.left.qualified_name,
                  definition.data.left.key_columns,
                ),
                right: sideLabel(
                  definition.data.right.qualified_name,
                  definition.data.right.key_columns,
                ),
              })}
            </p>
          )}
        </div>
        <div className="lq-page-header__actions">
          {/* Accent-filled and square as the reference sets it. Disabled while a request is in
              flight *and* while a run is — the server answers 409 for the second, and a button that
              fires a request the server will refuse teaches somebody to ignore the screen.
              Withheld entirely without the permission, which reads differently from disabled. */}
          {!mayRun ? (
            <span className="lq-rc__gated" title={t('reconciliation.requestRun.why')}>
              {t('reconciliation.requestRun.gated')}
            </span>
          ) : (
            <button
              type="button"
              className="lq-sect__action"
              disabled={
                request.isPending ||
                !definition.isSuccess ||
                (runs.isSuccess && runs.data.items.some((run) => isInFlight(run.state)))
              }
              onClick={() => {
                setQueued(null);
                request.mutate(definitionId, {
                  onSuccess: (run) => {
                    setQueued(run.id);
                  },
                });
              }}
            >
              {t('reconciliation.requestRun')}
            </button>
          )}
        </div>
      </header>
      <div className="lq-pagerule" />

      {queued !== null && (
        /* What the press actually queued, reported in place. The tolerance version is named because it
           is frozen onto the run: a later edit to the definition will not re-judge it, which is the
           one thing a reader may reasonably not expect. */
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">{t('reconciliation.queued.title')}</strong>
          <span className="lq-flash__body">
            {version === null
              ? t('reconciliation.queued.bodyNoVersion')
              : t('reconciliation.queued.body', { version: version.version })}
          </span>
        </div>
      )}

      {request.isError && (
        /* The server's own words. A 409 explains that a run is already in flight; a 422 names the
           similarity function the source lacks. Both are more useful than anything this screen could
           compose, and a fuzzy tolerance on an unsupported source is a refusal a reader has to act on
           by editing the definition. */
        <Alert tone="error" title={t('reconciliation.requestFailed')}>
          {request.error.message}
        </Alert>
      )}

      {definition.isError && (
        <Alert tone="error" title={t('reconciliation.loadFailed')}>
          {definition.error.message}
        </Alert>
      )}

      {definition.isPending && <SkeletonRows rows={3} columns={2} />}

      {definition.isSuccess && (
        <>
          <div className="lq-rc__panels">
            <TolerancePanel definition={definition.data} />
            <VocabularyPanel />
          </div>
          {/* The publish control sits with the tolerances it supersedes rather than in the header:
              it is a change to *this* version, and a header button would read as a page-level
              action. */}
          {mayPublish && definition.data.tolerance_version !== null && (
            <p className="lq-rc__publishLink">
              <button
                type="button"
                className="lq-linkbutton"
                onClick={() => {
                  setPublishOpen(true);
                }}
              >
                {t('reconciliation.publish.open')}
              </button>
            </p>
          )}
          <div className="lq-rc__panels">
            <BreakPolicyPanel
              definitionId={definitionId}
              canConfigure={mayConfigurePolicy}
            />
            <RunSchedulePanel
              definitionId={definitionId}
              canConfigure={mayConfigureSchedule}
            />
          </div>
          {definition.data.tolerance_version !== null && (
            <PublishToleranceDialog
              open={publishOpen}
              onClose={() => {
                setPublishOpen(false);
              }}
              definitionId={definitionId}
              current={definition.data.tolerance_version}
            />
          )}
        </>
      )}

      {runs.isSuccess && runs.data.truncated && (
        <div className="lq-band lq-band--warn">
          <div className="lq-band__title">{t('reconciliation.truncated.title')}</div>
          <div className="lq-band__body">{t('reconciliation.truncated.runs')}</div>
        </div>
      )}

      <SectionHead title={t('reconciliation.section.runs')} />

      {runs.isPending && <SkeletonRows rows={4} columns={6} />}

      {runs.isError && (
        <Alert tone="error" title={t('reconciliation.runsFailed')}>
          {runs.error.message}
        </Alert>
      )}

      {runs.isSuccess &&
        (runs.data.items.length === 0 ? (
          <EmptyState
            title={t('reconciliation.noRuns.title')}
            guidance={t('reconciliation.noRuns.body')}
          />
        ) : (
          <DataTable
            variant="ledger"
            caption={t('reconciliation.runsCaption', { name })}
            columns={[
              { key: 'requested', label: t('reconciliation.column.requested') },
              { key: 'state', label: t('reconciliation.column.state') },
              {
                key: 'outcome',
                label: t('reconciliation.column.outcome'),
                parts: [
                  t('reconciliation.column.outcome.matched'),
                  t('reconciliation.column.outcome.mismatched'),
                  t('reconciliation.column.outcome.missing'),
                ],
              },
              {
                key: 'jaccard',
                label: t('reconciliation.column.jaccard'),
                numeric: true,
              },
              { key: 'judgedBy', label: t('reconciliation.column.judgedBy') },
              { key: 'breaks', label: '', actions: true },
            ]}
          >
            {runs.data.items.map((run) => {
              const tint = rowTintOf(run.state);
              const openable = hasBreaks(run);
              const total = breakTotalOf(run);

              return (
                <tr
                  key={run.id}
                  className={
                    [
                      openable ? 'lq-rc__row' : null,
                      tint === null ? null : `lq-rc__row--${tint}`,
                    ]
                      .filter((name) => name !== null)
                      .join(' ') || undefined
                  }
                  onClick={
                    openable
                      ? () => {
                          navigate(breaksPath(definitionId, run.id));
                        }
                      : undefined
                  }
                >
                  <td className="lq-nowrap">
                    {new Date(run.requested_at).toLocaleString()}
                    <div className="lq-cell__secondary">
                      {t('reconciliation.requestedBy', { who: run.requested_by })}
                    </div>
                  </td>
                  <td className="lq-nowrap">
                    <RunStateTag state={run.state} />
                  </td>
                  <td>
                    <OutcomeCell run={run} />
                  </td>
                  <td className="lq-table__num">
                    <JaccardCell run={run} />
                  </td>
                  <td
                    className="lq-rc__version lq-mono lq-nowrap"
                    title={run.tolerance_version_id}
                  >
                    {/* The stored citation is a UUID, and a 36-character id in a scanned cell is
                            the bare-id defect this codebase has shipped four times. The version
                            *number* is what a reader wants — and it is only correct to print when the
                            run cites the version this screen is showing, which is the common case and
                            not a guarantee: an older run may cite a superseded version whose number
                            this route does not carry. So the number is shown when the ids match and
                            the id itself, truncated, when they do not. */}
                    {version !== null && version.id === run.tolerance_version_id
                      ? t('reconciliation.versionLabel', { version: version.version })
                      : `${run.tolerance_version_id.slice(0, 8)}…`}
                  </td>
                  <td className="lq-table__actions">
                    {openable ? (
                      <Link
                        className="lq-rowaction"
                        to={breaksPath(definitionId, run.id)}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {t('reconciliation.viewBreaks', {
                          count: total ?? 0,
                          shown: formatCount(total ?? 0),
                        })}
                      </Link>
                    ) : (
                      /* Said rather than left blank, and the four reasons are four different
                             facts — see `noBreaksReasonKey`. An empty cell would make a run that
                             matched every row look like one whose breaks failed to load. */
                      <span className="lq-muted" title={t(noBreaksReasonKey(run))}>
                        {t('reconciliation.noBreaks.label')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        ))}

      {runs.isSuccess && runs.data.items.length > 0 && (
        <p className="lq-note">{t('reconciliation.runs.foot')}</p>
      )}
      {runs.isSuccess && runs.data.items.some((run) => isRefusal(run.state)) && (
        /* Only when a refusal is actually on screen. A standing explanation of a state nobody is
           looking at is the noise that makes a reader stop reading the notes. */
        <p className="lq-note">{t('reconciliation.runs.refusalFoot')}</p>
      )}
      <DefinitionHistory definitionId={definitionId} />
    </div>
  );
}
